/**
 * SkeletonStickFigure.js — one body as a 3D stick figure (react-three-fiber).
 *
 * Takes ONE body explicitly: `joints` is a map { jointName: {x, y, z} } for a single person
 * (any naming convention — names are normalised via JOINT_ALIASES). Several people are drawn
 * by rendering several figures; split a multi-person frame with frameToBodies() first, so two
 * bodies can never merge into one figure.
 *
 * Colours come from EGDL tokens (useSkeletonColors), so they follow the light/dark theme:
 *   scheme 'neutral' — bones text-tertiary, joints text-primary, hands/wrists accent (design default)
 *   scheme 'region'  — bones and joints by body region (series palette)
 *   color="#hex"     — the whole figure in one colour (player identity colour)
 */
import { useMemo } from 'react';
import { Line, Sphere, Text } from '@react-three/drei';
import { useTokenColors } from './ThemeContext';
import {
    SKELETON_BONES,
    DEFAULT_JOINT_RADIUS,
    DEFAULT_BONE_THICKNESS,
    REGION_COLORS,
    buildJointNameMap,
    getBodyPartColorNormalized,
    resolveJointName,
} from './skeletonConstants';

const PARENT = Object.fromEntries(SKELETON_BONES.map((b) => [b.to, b.from]));
const HAND_JOINTS = new Set(['HandLeft', 'HandRight', 'HandTipLeft', 'HandTipRight', 'WristLeft', 'WristRight', 'ThumbLeft', 'ThumbRight']);
const TOKEN_NAMES = ['skel-bone', 'skel-joint', 'skel-hand', 'text-primary', 'background', ...Object.values(REGION_COLORS)];

/** Resolved hex colours for the skeleton roles and regions in the current theme. */
export function useSkeletonColors() {
    const t = useTokenColors(TOKEN_NAMES);
    return useMemo(() => {
        const region = {};
        Object.entries(REGION_COLORS).forEach(([r, tok]) => { region[r] = t[tok]; });
        return { bone: t['skel-bone'], joint: t['skel-joint'], hand: t['skel-hand'], label: t['text-primary'], labelOutline: t.background, region, token: (name) => t[name] };
    }, [t]);
}

/**
 * Split a frame into bodies. Accepts:
 *   { data: [{ bodyPart, position, body?|bodyId?|slot? }] }  (recorded skeleton frame)
 *   { bodies: [{ joints | data }] }                           (multi-body frame)
 *   { [jointName]: {x,y,z} }                                  (already one body)
 * Returns [{ id, joints: { name: {x,y,z} } }].
 */
export function frameToBodies(frame) {
    if (!frame) return [];
    if (Array.isArray(frame.bodies)) {
        return frame.bodies.map((b, i) => ({ id: b.id ?? b.slot ?? i, slot: b.slot, joints: b.joints || frameToBodies(b)[0]?.joints || {} }));
    }
    if (Array.isArray(frame.data)) {
        const groups = new Map();
        frame.data.forEach((j) => {
            if (!j || !j.bodyPart || !j.position) return;
            const id = j.body ?? j.bodyId ?? j.slot ?? 0;
            if (!groups.has(id)) groups.set(id, {});
            groups.get(id)[j.bodyPart] = j.position;
        });
        return [...groups.entries()].map(([id, joints]) => ({ id, joints }));
    }
    return [{ id: 0, joints: frame }];
}

function SkeletonStickFigure({
    joints,
    color = null,
    scheme = 'neutral',
    scale = 1.0,
    opacity = 1.0,
    showJointLabels = false,
    showJoints = true,
    showBones = true,
}) {
    const C = useSkeletonColors();
    const positions = useMemo(() => joints || {}, [joints]);

    const nameMap = useMemo(() => buildJointNameMap(Object.keys(positions)), [positions]);

    const boneSegments = useMemo(() => {
        const at = (canonical) => positions[nameMap.toActual[canonical]] || positions[canonical];
        const segments = [];
        SKELETON_BONES.forEach((bone) => {
            const b = at(bone.to);
            if (!b) return;
            // Bridge joints a tracker doesn't report (e.g. Unity rigs send no clavicle or wrist):
            // connect to the nearest present ancestor so limbs stay attached.
            let from = bone.from;
            let a = at(from);
            while (!a && PARENT[from]) {
                from = PARENT[from];
                a = at(from);
            }
            if (!a) return;
            segments.push({
                start: [a.x * scale, a.y * scale, a.z * scale],
                end: [b.x * scale, b.y * scale, b.z * scale],
                color: color || (scheme === 'region' ? C.region[bone.region] : C.bone),
            });
        });
        return segments;
    }, [positions, nameMap, scale, color, scheme, C]);

    const jointSpheres = useMemo(() => Object.entries(positions)
        .filter(([, p]) => p && Number.isFinite(p.x))
        .map(([bodyPart, p]) => {
            const canonical = resolveJointName(bodyPart);
            let c = color;
            if (!c) {
                if (scheme === 'region') c = C.token(getBodyPartColorNormalized(bodyPart)) || C.joint;
                else c = HAND_JOINTS.has(canonical) ? C.hand : C.joint;
            }
            return { position: [p.x * scale, p.y * scale, p.z * scale], color: c, bodyPart, head: canonical === 'Head' };
        }), [positions, scale, color, scheme, C]);

    if (!jointSpheres.length) return null;

    return (
        <group>
            {showBones && boneSegments.map((bone, i) => (
                <Line key={`bone-${i}`} points={[bone.start, bone.end]} color={bone.color} lineWidth={DEFAULT_BONE_THICKNESS * scale * 200} transparent opacity={opacity} />
            ))}
            {showJoints && jointSpheres.map((j, i) => (
                <Sphere key={`joint-${i}`} args={[DEFAULT_JOINT_RADIUS * scale * (j.head ? 1.6 : 1), 16, 16]} position={j.position}>
                    <meshBasicMaterial color={j.color} transparent opacity={opacity} />
                </Sphere>
            ))}
            {showJointLabels && jointSpheres.map((j, i) => (
                <Text key={`label-${i}`} position={[j.position[0], j.position[1] + 0.05 * scale, j.position[2]]} fontSize={0.03 * scale} color={C.label} anchorX="center" anchorY="middle" outlineWidth={0.002 * scale} outlineColor={C.labelOutline}>
                    {j.bodyPart}
                </Text>
            ))}
        </group>
    );
}

export default SkeletonStickFigure;
